import { useState } from "react";
import { toast } from "sonner";
import { Plus, CheckCircle2, Trash2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, PageState, StatusPill, Empty, Field } from "@/components/app/Common";
import { SimpleSelect, entityOptions, listOptions } from "@/components/app/SimpleSelect";
import { useLoad } from "@/hooks/useLoad";
import { api, errMsg } from "@/lib/api";
import { daysLabel, fmtDate } from "@/lib/ui";

const FILTERS = ["All", "Critical", "Overdue", "Due Soon", "Upcoming", "Compliant", "Dissolved"];
const RECUR = [{ value: "annual", label: "Annual" }, { value: "quarterly", label: "Quarterly" }, { value: "monthly", label: "Monthly" }, { value: "none", label: "One-time" }];

export const ObligationDialog = ({ open, onOpenChange, kind, ents, types, onSaved }) => {
  const isTax = kind === "tax";
  const [f, setF] = useState({ entity_id: "", title: "", type: "", due_date: "", recurrence: "annual", period: "" });
  const set = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e });
  const country = (ents || []).find((x) => x.id === f.entity_id)?.country;
  const typeOpts = isTax && country ? (types || []).filter((t) => (country === "Canada" ? ["T2", "GST/HST", "Payroll"] : ["Federal Return", "State Return", "Franchise Tax"]).includes(t)) : types;
  const save = async (e) => {
    e.preventDefault();
    if (!f.entity_id || !f.type) return toast.error("Choose an entity and type");
    try {
      if (isTax) await api.post("/taxes", { entity_id: f.entity_id, tax_type: f.type, due_date: f.due_date, period: f.period, recurrence: f.recurrence });
      else await api.post("/filings", { entity_id: f.entity_id, title: f.title || f.type, filing_type: f.type, due_date: f.due_date, recurrence: f.recurrence });
      toast.success("Deadline added");
      onSaved();
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`${kind}-dialog`}>
        <DialogHeader><DialogTitle>{isTax ? "Add tax deadline" : "Add filing"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          <Field label="Entity"><SimpleSelect value={f.entity_id} onChange={set("entity_id")} options={entityOptions(ents)} placeholder="Select entity" testId={`${kind}-entity-select`} /></Field>
          <Field label={isTax ? "Tax type" : "Filing type"}><SimpleSelect value={f.type} onChange={set("type")} options={listOptions(typeOpts)} placeholder="Select type" testId={`${kind}-type-select`} /></Field>
          {isTax ? <Field label="Period / description"><Input value={f.period} onChange={set("period")} data-testid="tax-period-input" /></Field>
            : <Field label="Title"><Input value={f.title} onChange={set("title")} placeholder="e.g. Ontario Annual Return" data-testid="filing-title-input" /></Field>}
          <div className="grid grid-cols-2 gap-4">
            <Field label="Due date"><Input required type="date" value={f.due_date} onChange={set("due_date")} data-testid={`${kind}-due-input`} /></Field>
            <Field label="Repeats"><SimpleSelect value={f.recurrence} onChange={set("recurrence")} options={RECUR} testId={`${kind}-recurrence-select`} /></Field>
          </div>
          <Button type="submit" className="w-full" data-testid={`${kind}-save-btn`}>Save</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function Compliance() {
  const state = useLoad(() => Promise.all([api.get("/filings"), api.get("/entities"), api.get("/meta")]).then(([a, b, c]) => ({ filings: a.data, ents: b.data, meta: c.data })));
  const [filter, setFilter] = useState("All");
  const [entity, setEntity] = useState("");
  const [open, setOpen] = useState(false);
  const all = state.data?.filings || [];
  const rows = all.filter((f) => (filter === "All" || f.status === filter) && (!entity || f.entity_id === entity));
  const count = (s) => all.filter((f) => f.status === s).length;
  const complete = async (f) => {
    const r = await api.post(`/filings/${f.id}/complete`);
    toast.success(r.data.next_due ? `Filed. Next due ${fmtDate(r.data.next_due)}` : "Marked as filed");
    state.reload();
  };
  const remove = async (f) => {
    await api.delete(`/filings/${f.id}`);
    state.reload();
  };
  return (
    <div>
      <PageHeader eyebrow="Module 3" title="Compliance management" subtitle="Annual returns, provincial, federal and state filings, franchise taxes, renewals and registered agent renewals. Every status feeds your health score.">
        <Button onClick={() => setOpen(true)} data-testid="add-filing-btn"><Plus className="h-4 w-4 mr-1" />Add filing</Button>
      </PageHeader>
      <PageState state={state}>
        <div className="flex flex-wrap gap-2 mb-5 items-center">
          {FILTERS.map((s) => (
            <button key={s} onClick={() => setFilter(s)} data-testid={`filter-${s.toLowerCase().replace(/\s+/g, "-")}`}
              className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${filter === s ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
              {s}{s !== "All" && <span className="ml-1.5 font-mono opacity-80">{count(s)}</span>}
            </button>
          ))}
          <div className="ml-auto w-56"><SimpleSelect value={entity} onChange={setEntity} allowNone noneLabel="All entities" options={entityOptions(state.data?.ents)} testId="filings-entity-filter" /></div>
        </div>
        {rows.length === 0 ? <Empty icon={ShieldCheck} title="Nothing here" text="No filings match this view." testId="filings-empty" /> : (
          <div className="surface overflow-x-auto" data-testid="filings-table">
            <table className="w-full text-sm">
              <thead className="text-xs text-muted-foreground border-b border-border">
                <tr>{["Filing", "Type", "Entity", "Due", "Status", ""].map((h) => <th key={h} className="text-left font-medium px-4 py-3">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((f) => (
                  <tr key={f.id} data-testid={`filing-row-${f.id}`}>
                    <td className="px-4 py-3 font-medium">{f.title}<div className="text-xs text-muted-foreground font-normal">{f.notes}</div></td>
                    <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{f.filing_type}</td>
                    <td className="px-4 py-3">{f.entity_name}</td>
                    <td className="px-4 py-3 whitespace-nowrap font-mono text-xs">{fmtDate(f.due_date)}{!f.completed_at && <div className="text-muted-foreground">{daysLabel(f.days_until)}</div>}</td>
                    <td className="px-4 py-3"><StatusPill status={f.status} testId={`filing-status-${f.id}`} /></td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      {!f.completed_at && f.status !== "Dissolved" && <Button size="sm" variant="outline" onClick={() => complete(f)} data-testid={`filing-complete-${f.id}`}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />Mark filed</Button>}
                      <Button size="icon" variant="ghost" onClick={() => remove(f)} data-testid={`filing-delete-${f.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <ObligationDialog open={open} onOpenChange={setOpen} kind="filing" ents={state.data?.ents} types={state.data?.meta?.filing_types} onSaved={state.reload} />
      </PageState>
    </div>
  );
}
